import { useCallback, useReducer } from "react";

import type { DefaultDocument } from "~/shared/types/payment-types";

export type DocumentViewerState = {
	isOpen: boolean;
	selectedDocument: {
		documentID: number;
		documentDescription: string;
		documentFileName: string;
		categoryDocumentID: number;
	} | null;
};

const defaultDocument = {
	documentID: 0,
	documentDescription: "",
	documentFileName: "",
	categoryDocumentID: 0,
};

export const initialDocumentViewerState: DocumentViewerState = {
	isOpen: false,
	selectedDocument: defaultDocument,
};

export type DocumentViewerAction =
	| { type: "open" }
	| { type: "close"; selectedDocument: typeof defaultDocument }
	| { type: "select"; selectedDocument: typeof defaultDocument };

export const documentViewerReducer = (
	state: DocumentViewerState,
	action: DocumentViewerAction,
): DocumentViewerState => {
	switch (action.type) {
		case "open": {
			return { ...state, isOpen: true, selectedDocument: defaultDocument };
		}
		case "close": {
			return { ...state, isOpen: false, selectedDocument: defaultDocument };
		}
		case "select": {
			return { ...state, selectedDocument: action.selectedDocument };
		}
		default: {
			return state;
		}
	}
};

export const useDocumentViewer = () => {
	const [viewerState, dispatch] = useReducer(
		documentViewerReducer,
		initialDocumentViewerState,
	);

	const openViewer = useCallback(() => {
		dispatch({ type: "open" });
	}, []);

	const closeViewer = useCallback(() => {
		dispatch({ type: "close", selectedDocument: defaultDocument });
	}, []);

	const selectDocument = useCallback((document: DefaultDocument) => {
		dispatch({ type: "select", selectedDocument: document });
	}, []);

	return {
		viewerState,
		openViewer,
		closeViewer,
		selectDocument,
	};
};
